/**
 * Realsee SSO + Shopify Multipass 登录/登出模块
 *
 * 依赖：window.RealseeAuth（来自 yg-realsee-auth.js）
 *
 * 场景一：Shopify 已登录 + SSO 已登录 → 点击登出
 *   跳转 SSO logout，并在 redirect 中串联 Shopify /account/logout
 *
 * 场景二/三：Shopify 未登录 → 点击登录
 *   调 API 获取 source_verify → 拼登录 URL → 跳转 SSO 登录页
 *
 * 场景四：Shopify 已登录，页面加载时静默验证 SSO cookie + user_id 匹配
 *   先调 /api/customer/query 拿 user_id，再调 /open/v1/verify/user 校验
 */

(function (window) {
  'use strict';

  var isLoginRedirecting = false;

  // ─── 配置常量 ─────────────────────────────────────────────────────────────
  var SSO_CONFIG = {
    endpoints: {
      passportBase: 'https://my.realsee.ai',
      proxyBase: 'https://i-shopify-app.realsee.ai',
      loginPageUrl: 'https://login.realsee.ai/shopify',
      logoutPageUrl: 'https://my.realsee.ai/logout'
    },
    auth: {
      source: 'SSO_HOME_APP'
    },
    cookies: {
      realseeToken: 'realsee_token',
      shopifyEssential: '_shopify_essential'
    }
  };

  // ─── Cookie 工具函数 ───────────────────────────────────────────────────────

  /**
   * 增强版 Cookie 获取函数 - 优化版
   * 支持详细调试信息，处理正则转义，并打印所有相关 cookies
   */
  function getCookie(name) {
    console.log(
      `[Cookie] 尝试读取: ${name}, 当前域名: ${window.location.hostname}`
    );
    console.log(
      `[Cookie] document.cookie 长度: ${document.cookie ? document.cookie.length : 0}`
    );

    // 对 name 进行正则转义，防止特殊字符导致匹配失败
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var match = document.cookie.match(
      new RegExp('(?:^|; )' + escapedName + '=([^;]*)')
    );

    if (match) {
      const value = decodeURIComponent(match[1]);
      console.log(
        `[Cookie] ✓ 成功读取 ${name}:`,
        value.substring(0, 40) + (value.length > 40 ? '...' : '')
      );
      return value;
    }

    // 打印所有可能相关的 cookies，帮助诊断问题
    console.log('[Cookie] 当前页面所有 cookies:');
    if (document.cookie) {
      const allCookies = document.cookie.split(';').map((c) => c.trim());
      allCookies.forEach((cookie) => {
        if (
          cookie.toLowerCase().includes('token') ||
          cookie.toLowerCase().includes('realsee') ||
          cookie.toLowerCase().includes('auth') ||
          cookie.toLowerCase().includes('session')
        ) {
          console.log(
            `   → ${cookie.substring(0, 90)}${cookie.length > 90 ? '...' : ''}`
          );
        }
      });
    } else {
      console.log('   → document.cookie 为空');
    }

    console.warn(
      `[Cookie] ✗ 未找到 cookie: ${name} (当前域名可能无法访问该cookie)`
    );
    return null;
  }

  /**
   * 删除指定 cookie，覆盖当前域名和根域两种情况
   */
  function deleteCookie(name) {
    var expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    var hostname = window.location.hostname;

    // 当前完整域名
    document.cookie = name + '=; ' + expired + '; domain=' + hostname;
    // 根域（如 .realsee.ai）
    var parts = hostname.split('.');
    if (parts.length > 2) {
      var rootDomain = '.' + parts.slice(-2).join('.');
      document.cookie = name + '=; ' + expired + '; domain=' + rootDomain;
    }
    // 不带 domain（兜底）
    document.cookie = name + '=; ' + expired;
  }

  function getShopifyLogoutUrl() {
    var shopifyOrigin =
      window.YGSSOConfig && window.YGSSOConfig.shopifyOrigin
        ? window.YGSSOConfig.shopifyOrigin
        : window.location.origin;
    var returnUrl = encodeURIComponent(window.location.href);

    return (
      shopifyOrigin.replace(/\/$/, '') +
      '/account/logout?return_url=' +
      returnUrl
    );
  }

  function redirectToUnifiedLogout() {
    var redirectParam = encodeURIComponent(getShopifyLogoutUrl());
    window.location.href =
      SSO_CONFIG.endpoints.logoutPageUrl + '?redirect=' + redirectParam;
  }

  // ─── API 函数 ──────────────────────────────────────────────────────────────

  /**
   * 获取 SSO source_verify ticket（场景二/三登录跳转用）
   * @returns {Promise<string|null>} ticket 字符串，失败返回 null
   */
  async function getTicket() {
    try {
      console.log('请求了 getToken ===>');
      var accessToken = await window.RealseeAuth.getToken();
      console.log('accessToken===>', accessToken);
      var signedHeaders = await window.RealseeAuth.getAuthHeaders();
      console.log('signedHeaders===>', signedHeaders);
      var res = await fetch(
        SSO_CONFIG.endpoints.proxyBase +
          '/api/proxy/passport/api/v1/session/apply/ticket/page?source=SSO_HOME_APP',
        {
          method: 'POST',
          credentials: 'include',
          headers: Object.assign({}, signedHeaders, {
            Authorization: accessToken,
            Accept: '*/*'
          })
        }
      );

      console.log('getTicket res===>', res);
      console.log('getTicket res.ok===>', res.ok);

      if (!res.ok) {
        console.warn('[YG SSO] getTicket failed, status:', res.status);
        return null;
      }

      var data = await res.json();
      console.log('getTicket data===>', data);
      if (data.code === 0 && data.data && data.data.ticket) {
        return data.data.ticket;
      }
      console.warn('[YG SSO] getTicket unexpected response:', data);
      return null;
    } catch (err) {
      console.warn('[YG SSO] getTicket error:', err);
      return null;
    }
  }

  /**
   * 统一登录重定向（复用场景二/三与场景四）
   * @returns {Promise<boolean>} 是否成功发起重定向
   */
  async function redirectToLoginWithTicket(redirectTargetUrl) {
    if (isLoginRedirecting) {
      return false;
    }

    isLoginRedirecting = true;

    try {
      var ticket = await getTicket();
      if (!ticket) {
        console.warn('[YG SSO] getTicket failed, abort login redirect');
        isLoginRedirecting = false;
        return false;
      }

      var redirectUrl = encodeURIComponent(
        redirectTargetUrl || window.location.href
      );

      var loginUrl =
        SSO_CONFIG.endpoints.loginPageUrl +
        '?source=' +
        SSO_CONFIG.auth.source +
        '&source_verify=' +
        encodeURIComponent(ticket) +
        '&region=' +
        '&redirect=' +
        redirectUrl;

      console.log('=====loginUrl=====', loginUrl);
      window.location.href = loginUrl;
      return true;
    } catch (err) {
      console.warn('[YG SSO] redirectToLoginWithTicket error:', err);
      isLoginRedirecting = false;
      return false;
    }
  }

  /**
   * 先清理 SSO 会话，再回到 Shopify /account/logout（场景四 invalid 分支）
   * @returns {Promise<boolean>} 是否成功发起重定向
   */
  async function redirectToLogoutThenShopifyLogout() {
    if (isLoginRedirecting) {
      return false;
    }

    isLoginRedirecting = true;

    try {
      var logoutThenShopifyLogoutUrl =
        SSO_CONFIG.endpoints.logoutPageUrl +
        '?redirect=' +
        encodeURIComponent(getShopifyLogoutUrl());
      console.log(
        '[YG SSO] redirectToLogoutThenShopifyLogout url:',
        logoutThenShopifyLogoutUrl
      );
      window.location.href = logoutThenShopifyLogoutUrl;
      return true;
    } catch (err) {
      console.warn('[YG SSO] redirectToLogoutThenShopifyLogout error:', err);
      isLoginRedirecting = false;
      return false;
    }
  }

  /**
   * 通过 Shopify customer id 查询 customer 详情，并提取用于 verify/user 的 user_id
   * @param {string|number} shopifyCustomerId
   * @returns {Promise<string|null>}
   */
  async function getRealseeUserId(shopifyCustomerId) {
    if (!shopifyCustomerId) {
      return null;
    }

    try {
      var customerGid =
        String(shopifyCustomerId).indexOf('gid://shopify/Customer/') === 0
          ? String(shopifyCustomerId)
          : 'gid://shopify/Customer/' + String(shopifyCustomerId);
      var accessToken = await window.RealseeAuth.getToken();
      var signedHeaders = await window.RealseeAuth.getAuthHeaders();
      var res = await fetch(
        SSO_CONFIG.endpoints.proxyBase +
          '/api/customer/query?user_id=' +
          encodeURIComponent(customerGid),
        {
          method: 'GET',
          credentials: 'include',
          headers: Object.assign({}, signedHeaders, {
            Authorization: accessToken,
            Accept: '*/*'
          })
        }
      );

      if (!res.ok) {
        console.warn('[YG SSO] getRealseeUserId failed, status:', res.status);
        return null;
      }

      var data = await res.json();
      var realseeUserId =
        data &&
        data.code === 0 &&
        data.data &&
        (data.data.user_id != null || data.data.multipass_identifier != null)
          ? String(
              data.data.user_id != null
                ? data.data.user_id
                : data.data.multipass_identifier
            )
          : '';

      if (!realseeUserId) {
        console.warn('[YG SSO] getRealseeUserId: missing user_id in response');
        return null;
      }

      return realseeUserId;
    } catch (err) {
      console.warn('[YG SSO] getRealseeUserId error:', err);
      return null;
    }
  }

  /**
   * 使用 login 域校验当前 cookie 对应用户是否与 user_id 匹配
   * @param {string} userId
   * @returns {Promise<'valid'|'invalid'|'unknown'>}
   */
  async function verifyCurrentLoginUser(userId) {
    if (!userId) {
      return 'unknown';
    }

    console.log('[YG SSO] 开始请求了===>userId', userId);

    try {
      var verifyUrl =
        'https://login.realsee.ai/open/v1/verify/user' +
        '?user_id=' +
        encodeURIComponent(userId);
      console.log('[YG SSO] verifyCurrentLoginUser request:', {
        userId: String(userId),
        url: verifyUrl,
        credentials: 'include'
      });

      var res = await fetch(verifyUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: '*/*'
        }
      });
      console.log('[YG SSO] verifyCurrentLoginUser response status:', {
        ok: res.ok,
        status: res.status,
        statusText: res.statusText
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          console.warn(
            '[YG SSO] verifyCurrentLoginUser judged as invalid by status:',
            res.status
          );
          return 'invalid';
        }

        console.warn(
          '[YG SSO] verifyCurrentLoginUser unexpected status:',
          res.status
        );
        return 'unknown';
      }

      var data = await res.json();
      console.log('[YG SSO] verifyCurrentLoginUser response payload:', data);

      // verify/user 当前返回结构：
      // { code: 100000, data: { is_login: boolean }, msg: "" }
      // 先以 is_login 为准，避免把业务成功码误判为 invalid。
      if (data && data.data && typeof data.data.is_login === 'boolean') {
        var statusByIsLogin = data.data.is_login ? 'valid' : 'invalid';
        console.log(
          '[YG SSO] verifyCurrentLoginUser judged by data.is_login:',
          {
            is_login: data.data.is_login,
            status: statusByIsLogin
          }
        );
        return statusByIsLogin;
      }

      // 兼容其他可能结构
      if (data && data.data && typeof data.data.valid === 'boolean') {
        var statusByValid = data.data.valid ? 'valid' : 'invalid';
        console.log('[YG SSO] verifyCurrentLoginUser judged by data.valid:', {
          valid: data.data.valid,
          status: statusByValid
        });
        return statusByValid;
      }

      if (data && typeof data.success === 'boolean') {
        var statusBySuccess = data.success ? 'valid' : 'invalid';
        console.log('[YG SSO] verifyCurrentLoginUser judged by success:', {
          success: data.success,
          status: statusBySuccess
        });
        return statusBySuccess;
      }

      if (data && typeof data.code === 'number') {
        if (data.code === 0) {
          console.log(
            '[YG SSO] verifyCurrentLoginUser judged by code=0 as valid'
          );
          return 'valid';
        }
        // 100000 常见于请求成功但未携带可判定登录态字段的场景
        if (data.code === 100000) {
          console.warn(
            '[YG SSO] verifyCurrentLoginUser code=100000 without login field, fallback to unknown'
          );
          return 'unknown';
        }
        console.warn(
          '[YG SSO] verifyCurrentLoginUser judged as invalid by code:',
          data.code
        );
        return 'invalid';
      }

      console.warn(
        '[YG SSO] verifyCurrentLoginUser unknown response structure:',
        data
      );
      return 'unknown';
    } catch (err) {
      console.warn('[YG SSO] verifyCurrentLoginUser error:', err);
      return 'unknown';
    }
  }

  // ─── 场景处理函数 ──────────────────────────────────────────────────────────

  /**
   * 场景一：已登录 → 登出
   * 直接跳转 SSO 登出页，并由 redirect 串联 Shopify /account/logout
   */
  function handleLogout(e) {
    e.preventDefault();
    redirectToUnifiedLogout();
  }

  /**
   * 场景二/三：未登录 → 登录
   * 1. 调 getTicket() 获取 source_verify
   * 2. redirect 使用 Shopify 站点根地址
   * 3. 跳转至 SSO 登录页（SSO 侧若已登录则自动重定向回 Shopify）
   */
  async function handleLogin(e) {
    e.preventDefault();
    var redirected = await redirectToLoginWithTicket();
    if (!redirected) {
      window.alert('SSO login initialization failed. Please try again.');
    }
  }

  function disableEarlyCheckoutGuard() {
    if (
      window.YGSSOEarlyCheckoutGuard &&
      typeof window.YGSSOEarlyCheckoutGuard.disable === 'function'
    ) {
      window.YGSSOEarlyCheckoutGuard.disable();
    }
  }

  function reportTelemetryUserInfo(userId) {
    if (
      !userId ||
      !window.RealseeShopTelemetry ||
      typeof window.RealseeShopTelemetry.setUserId !== 'function'
    ) {
      return;
    }

    window.RealseeShopTelemetry.setUserId(String(userId));
  }

  function bindCheckoutToSSOLogin(config) {
    if (config.isLoggedIn) {
      disableEarlyCheckoutGuard();
      return;
    }

    // Intercept cart form submits where submitter is checkout button.
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || form.tagName !== 'FORM') return;

        var submitter = event.submitter;
        if (!submitter) return;

        var submitterName = (
          submitter.getAttribute('name') || ''
        ).toLowerCase();
        if (submitterName !== 'checkout') return;

        handleLogin(event);
      },
      true
    );

    // Intercept direct checkout links and checkout-like buttons.
    document.addEventListener(
      'click',
      function (event) {
        var trigger = event.target.closest(
          'a[href$="/checkout"], a[href*="/checkout?"], button[name="checkout"], input[name="checkout"]'
        );

        if (!trigger) return;

        // Do not hijack accelerated checkout widgets.
        if (
          trigger.closest(
            '.additional-checkout-buttons, .shopify-payment-button'
          )
        ) {
          return;
        }

        handleLogin(event);
      },
      true
    );

    // 真实拦截器已生效，移除内联早期兜底拦截器，避免重复处理。
    disableEarlyCheckoutGuard();
  }

  /**
   * 场景四：页面加载时静默检测（仅 Shopify 已登录时执行）
   * 通过 verify/user 校验 login cookie 与 customer 关联 user_id 是否匹配
   */
  async function initScenario4() {
    var config = window.YGSSOConfig || {};
    var shopifyCustomerId =
      config.customer && config.customer.id != null ? config.customer.id : null;
    console.log('[YG SSO] Scenario 4 start:', {
      isLoggedIn: !!config.isLoggedIn,
      shopifyCustomerId: shopifyCustomerId
    });

    if (!shopifyCustomerId) {
      console.warn(
        '[YG SSO] Scenario 4 skipped because shopify customer id is missing'
      );
      return;
    }

    var realseeUserId = await getRealseeUserId(shopifyCustomerId);
    console.log('[YG SSO] Scenario 4 customer mapping result:', {
      shopifyCustomerId: shopifyCustomerId,
      realseeUserId: realseeUserId
    });
    if (!realseeUserId) {
      console.warn('[YG SSO] Scenario 4 skipped because user_id query failed');
      return;
    }

    reportTelemetryUserInfo(realseeUserId);

    console.log('[YG SSO] 开始请求了===>realseeUserId', realseeUserId);

    var verifyStatus = await verifyCurrentLoginUser(realseeUserId);
    console.log('[YG SSO] Scenario 4 verify result:', {
      realseeUserId: realseeUserId,
      verifyStatus: verifyStatus
    });
    if (verifyStatus === 'invalid') {
      console.warn(
        '[YG SSO] Scenario 4: is_login=false, redirecting to logout then /account/logout'
      );
      var redirectedByLogout = await redirectToLogoutThenShopifyLogout();
      if (!redirectedByLogout) {
        console.warn('[YG SSO] Scenario 4: logout redirect was not triggered');
      }
      return;
    }

    if (verifyStatus === 'unknown') {
      console.warn(
        '[YG SSO] Scenario 4: verify unknown, redirecting to login with ticket'
      );
      var redirected = await redirectToLoginWithTicket();
      if (!redirected) {
        console.warn(
          '[YG SSO] Scenario 4: login redirect was not triggered after verify failure'
        );
      }
      return;
    }
  }

  // ─── 初始化 ────────────────────────────────────────────────────────────────

  window.YGSSOLogin = window.YGSSOLogin || {};
  window.YGSSOLogin.triggerLogin = redirectToLoginWithTicket;

  document.addEventListener('DOMContentLoaded', function () {
    var config = window.YGSSOConfig || {};

    // 场景四：Shopify 已登录时，静默验证 SSO token 有效性
    if (config.isLoggedIn) {
      initScenario4();
    }

    bindCheckoutToSSOLogin(config);

    // 绑定登录/登出按钮事件（支持多个触发器）
    var authTriggers = document.querySelectorAll('[data-yg-action]');
    if (!authTriggers.length) return;

    authTriggers.forEach(function (trigger) {
      if (trigger.dataset.ygAction === 'login') {
        trigger.addEventListener('click', handleLogin);
      } else if (trigger.dataset.ygAction === 'logout') {
        trigger.addEventListener('click', handleLogout);
      }
    });
  });
})(window);
